import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSEO, generateWebsiteJsonLd, generateGameJsonLd, generateOrganizationJsonLd, generateSoftwareApplicationJsonLd } from '@/hooks/useSEO';
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { ChessBoard } from "@/components/ChessBoard";
import { MultiplayerChessBoard } from "@/components/MultiplayerChessBoard";
import { BotMultiplayerBoard } from "@/components/BotMultiplayerBoard";
import { ChessEngineWidget } from "@/components/ChessEngineWidget";
import { MatchmakingDialog } from "@/components/MatchmakingDialog";
import { BotChallengeDialog } from "@/components/BotChallengeDialog";
import { ChessAppLayout } from "@/components/ChessAppLayout";
import { ChallengeAISquad } from "@/components/ChallengeAISquad";
import { GameModeActions } from "@/components/GameModeActions";
import { BotChallengeSystem } from "@/components/BotChallengeSystem";
import { InteractiveHeroSection } from "@/components/InteractiveHeroSection";
import { FeatureShowcase } from "@/components/FeatureShowcase";
import { ChessFactsSection } from "@/components/ChessFactsSection";
import { QuickStartSection } from "@/components/QuickStartSection";
import { MiniChessDemo } from "@/components/MiniChessDemo";
import { TrustSignals } from "@/components/TrustSignals";
import { TestimonialsSection } from "@/components/TestimonialsSection";
import { GuestModeHero } from "@/components/GuestModeHero";
import { PrivacyBanner } from "@/components/PrivacyBanner";
import { QuickPlayButton } from "@/components/QuickPlayButton";
import { OnboardingTrigger } from "@/components/OnboardingTrigger";
import { useMultiplayerGame } from "@/hooks/useMultiplayerGame";
import { useMatchmaking } from "@/hooks/useMatchmaking";
import { useAuth } from "@/hooks/useAuth";
import { useIsMobile } from "@/hooks/use-mobile";
import { ArrowLeft, AlertCircle } from "lucide-react";
import { useNavigate as useRouterNavigate } from "react-router-dom";
import { computerOpponents, BotData, getBotNameByElo } from "@/lib/bot-constants";
import { HatchEvolutionBoard } from "@/components/HatchEvolutionBoard";
import { DirectGameBoard } from "@/components/DirectGameBoard";

type GameState = 'menu' | 'game' | 'multiplayer' | 'engine' | 'bot-multiplayer' | 'hatch-selection' | 'hatch-game' | 'direct-game';
type GameMode = 'classic' | 'hatch';

const Index = () => {
  // SEO Configuration
  useSEO({
    title: "ChessHatch - Advanced AI Chess | Hatch Evolution & Adaptive Bot Opponents",
    description: "Experience next-gen chess with ChessHatch! Advanced AI bots with unique personalities, innovative Hatch Evolution gameplay, and adaptive opponents. Perfect for all skill levels!",
    keywords: "AI chess bots, hatch evolution chess, adaptive AI opponents, chess bot challenges, advanced chess AI, chess personality bots, evolutionary chess",
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        generateWebsiteJsonLd(),
        generateGameJsonLd()
      ]
    }
  });

  const [gameState, setGameState] = useState<GameState>('menu');
  const [currentGameMode, setCurrentGameMode] = useState<GameMode>('classic');
  const [opponent, setOpponent] = useState<{ name: string; isBot: boolean; elo?: number; timeControl?: string } | null>(null);
  const [showMatchmaking, setShowMatchmaking] = useState(false);
  const [showBotChallenge, setShowBotChallenge] = useState(false);
  const [selectedBot, setSelectedBot] = useState<BotData | null>(null);
  const [filteredBots, setFilteredBots] = useState<BotData[]>(computerOpponents);
  const [botGame, setBotGame] = useState<{ 
    roomCode: string; 
    timeControl: string; 
    botElo: number; 
    botName: string;
    botData: BotData;
    playerColor: 'white' | 'black';
    isRated: boolean;
  } | null>(null);
  const [directGameSession, setDirectGameSession] = useState<{
    gameSessionId: string;
    playerColor: 'white' | 'black';
  } | null>(null);
  const { user } = useAuth();
  const navigate = useNavigate();
  const routerNavigate = useRouterNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  
  const {
    room,
    gameSession,
    participants,
    playerColor,
    isConnected,
    createRoom,
    joinRoom,
    joinPersistentRoom,
    makeGameMove,
    resignGame,
    offerDraw,
    leaveRoom
  } = useMultiplayerGame();

  const { checkExistingState } = useMatchmaking();
  const [hasCheckedRejoin, setHasCheckedRejoin] = useState(false);
  const [intentionalExit, setIntentionalExit] = useState(false);
  const [showAbandonConfirm, setShowAbandonConfirm] = useState(false);

  // Check and rejoin active games/queue only once on mount
  useEffect(() => {
    const checkAndRejoin = async () => {
      const state = await checkExistingState();
      
      if (state?.type === 'active_game') {
        const data = state.data as { game_session_id: string; time_control: string; player_color: 'white' | 'black' };
        setDirectGameSession({
          gameSessionId: data.game_session_id,
          playerColor: data.player_color
        });
        setGameState('direct-game');
      } else if (state?.type === 'queue') {
        setShowMatchmaking(true);
      }
      setHasCheckedRejoin(true);
    };

    if (!hasCheckedRejoin && user && gameState === 'menu' && !intentionalExit) {
      checkAndRejoin();
    }
  }, [user, gameState, hasCheckedRejoin, intentionalExit, checkExistingState]);

  // Handle room joining via URL parameters and game mode selection
  useEffect(() => {
    (async () => {
      const searchParams = new URLSearchParams(location.search);
      const joinRoomCode = searchParams.get('join');
      const gameId = searchParams.get('game');
      const modeParam = searchParams.get('mode');
      const sectionParam = searchParams.get('section');
      
      if (gameId && user) {
        // Validate UUID format before proceeding
        const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
        if (uuidRegex.test(gameId)) {
          console.log('[Index] Loading direct game session:', gameId);
          setDirectGameSession({ gameSessionId: gameId, playerColor: 'white' }); // Color determined by hook
          setGameState('direct-game');
        } else {
          console.error('[Index] Invalid game ID format:', gameId);
        }
        navigate('/', { replace: true });
      } else if (joinRoomCode && user) {
        const normalized = joinRoomCode.trim().toUpperCase();
        console.log('[JoinViaURL] Attempting to join room with code:', normalized);
        const joined = await joinRoom(normalized);
        // Clean the URL after attempt
        navigate('/', { replace: true });
        if (joined) {
          setGameState('multiplayer');
        }
      } else if (modeParam === 'hatch') {
        console.log('[Index] Starting Hatch Evolution mode');
        setCurrentGameMode('hatch');
        setGameState('hatch-selection');
      } else if (sectionParam === 'ai-squad') {
        console.log('[Index] Opening AI Squad bot selection dialog');
        // Open bot challenge dialog with no pre-selected bot
        setSelectedBot(null);
        setShowBotChallenge(true);
        // Clean the URL after opening dialog
        navigate('/', { replace: true });
      }
    })();
  }, [location.search, user, navigate]);

  const startGame = (mode: GameMode, opponentName: string, isBot: boolean = false, elo?: number, timeControl?: string) => {
    setCurrentGameMode(mode);
    setOpponent({ name: opponentName, isBot, elo, timeControl });
    setGameState('game');
  };

  const backToMenu = () => {
    setIntentionalExit(true);
    setGameState('menu');
    setDirectGameSession(null);
    setBotGame(null);
    leaveRoom();
  };

  const handleBackToMenu = async () => {
    // Check if there's an active game
    if (gameState === 'direct-game' && directGameSession) {
      // For ongoing games, show confirmation
      if (gameSession?.game_status === 'ongoing') {
        setShowAbandonConfirm(true);
        return;
      }
    }
    
    // For completed games or other states, go back directly
    backToMenu();
  };

  const confirmAbandonGame = () => {
    setShowAbandonConfirm(false);
    backToMenu();
  };

  const handleCreateRoom = async (roomCode: string, timeControl: string) => {
    const success = await createRoom(roomCode, timeControl);
    if (success) {
      setGameState('multiplayer');
    }
  };

  const handleJoinRoom = async (roomCode: string) => {
    const success = await joinRoom(roomCode);
    if (success) {
      setGameState('multiplayer');
    }
  };

  const handleJoinPublicRoom = async (roomCode: string) => {
    // This handles both temporary and persistent room joining through the unified joinRoom function
    const success = await joinRoom(roomCode);
    if (success) {
      setGameState('multiplayer');
    }
  };

  const handleJoinPersistentRoom = async (roomCode: string) => {
    // Use the unified room joining function
    const success = await joinRoom(roomCode);
    
    if (success) {
      setGameState('multiplayer');
    }
  };

  const handleStartMatchmaking = () => {
    setShowMatchmaking(true);
  };

  const handleShowBotChallenge = (bot: BotData | null) => {
    setSelectedBot(bot);
    setShowBotChallenge(true);
  };

  const handleGenericBotChallenge = (botNames?: string[]) => {
    setSelectedBot(null);
    if (botNames && botNames.length > 0) {
      const filtered = computerOpponents.filter(bot => botNames.includes(bot.name));
      setFilteredBots(filtered);
    } else {
      setFilteredBots(computerOpponents);
    }
    setShowBotChallenge(true);
  };

  const handleBotChallenge = (bot: BotData, timeControl: string, playerColor: 'white' | 'black', isRated: boolean) => {
    // Generate a unique room code for bot games
    const roomCode = `BOT-${Math.random().toString(36).slice(2, 8).toUpperCase()}`;
    
    // Use Bot Multiplayer flow to ensure proper game session and currency rewards
    setBotGame({
      roomCode,
      timeControl,
      botElo: bot.elo,
      botName: bot.name,
      botData: bot,
      playerColor,
      isRated
    });
    setGameState('bot-multiplayer');
    setShowBotChallenge(false);
    setSelectedBot(null);
  };

  const handleMatchFound = (
    gameSessionId: string,
    timeControl: string,
    playerColor: 'white' | 'black',
    isBot?: boolean,
    botElo?: number,
    fakeOpponent?: { username: string; elo: number }
  ) => {
    if (isBot) {
      // Use fake opponent data if available (from matchmaking), otherwise extract from session ID
      const eloMatch = gameSessionId.match(/ELO(\d+)/);
      const extractedElo = fakeOpponent?.elo || (eloMatch ? parseInt(eloMatch[1]) : botElo);
      
      const botData = computerOpponents.find(b => b.elo === (extractedElo || 1200)) || computerOpponents[3];
      setBotGame({
        roomCode: gameSessionId,
        timeControl,
        botElo: extractedElo || 1200,
        botName: fakeOpponent?.username || getBotNameByElo(extractedElo || 1200),
        botData,
        playerColor: playerColor,
        isRated: true
      });
      setGameState('bot-multiplayer');
    } else {
      // Direct game session for human matchmaking
      console.log('[Index] Match found - Direct game session:', gameSessionId);
      setDirectGameSession({ 
        gameSessionId, 
        playerColor 
      });
      setGameState('direct-game');
    }
    setShowMatchmaking(false);
  };

  if (gameState === 'game') {
    return (
      <ChessAppLayout showGameOptions={false} showFooter={false}>
        <div className="h-full flex flex-col">
          <div className="p-4 border-b border-border">
            <Button 
              variant="outline" 
              onClick={backToMenu}
              size="sm"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Menu
            </Button>
          </div>
          
           <div className="flex-1 overflow-auto">
            {opponent && (
              <ChessBoard
                gameMode={currentGameMode}
                opponent={opponent}
                timeControl={opponent.timeControl || "5+0"}
                onResign={backToMenu}
              />
            )}
          </div>
        </div>
      </ChessAppLayout>
    );
  }

  if (gameState === 'multiplayer') {
    return (
      <ChessAppLayout showGameOptions={false} showFooter={false}>
        <div className="h-full flex flex-col">
          <div className="p-4 border-b border-border">
            <Button 
              variant="outline" 
              onClick={backToMenu}
              size="sm"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Menu
            </Button>
          </div>
          
          <div className="flex-1 max-h-screen overflow-y-auto">
            <div className="p-4 space-y-6">
              <MultiplayerChessBoard
              room={room}
              gameSession={gameSession}
              participants={participants}
              playerColor={playerColor}
              isConnected={isConnected}
              onMove={makeGameMove}
              onLeaveRoom={backToMenu}
              onResign={resignGame}
              onOfferDraw={offerDraw}
              />
            </div>
          </div>
        </div>
      </ChessAppLayout>
    );
  }

  if (gameState === 'bot-multiplayer') {
    return (
      <ChessAppLayout showGameOptions={false} showFooter={false}>
        <div className="h-full flex flex-col">
          <div className="p-4 border-b border-border">
            <Button 
              variant="outline" 
              onClick={backToMenu}
              size="sm"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Menu
            </Button>
          </div>
          
          <div className="flex-1 overflow-auto">
            {botGame && (
              <BotMultiplayerBoard
                roomCode={botGame.roomCode}
                timeControl={botGame.timeControl}
                botElo={botGame.botElo}
                botName={botGame.botName}
                botAvatarConfig={botGame.botData.avatarConfig}
                botPersonality={botGame.botData.personality}
                botPlayStyle={botGame.botData.playStyle}
                playerColor={botGame.playerColor}
                isRated={botGame.isRated}
                onLeaveRoom={backToMenu}
              />
            )}
          </div>
        </div>
      </ChessAppLayout>
    );
  }

  if (gameState === 'engine') {
    return (
      <ChessAppLayout showGameOptions={false} showFooter={false}>
        <div className="h-full flex flex-col">
          <div className="p-4 border-b border-border">
            <Button 
              variant="outline" 
              onClick={backToMenu}
              size="sm"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Menu
            </Button>
          </div>
          
          <div className="flex-1 overflow-hidden p-6">
            <ChessEngineWidget />
          </div>
        </div>
      </ChessAppLayout>
    );
  }

  // Hatch Evolution Bot Selection
  if (gameState === 'hatch-selection') {
    return (
      <ChessAppLayout showGameOptions={false} showFooter={false}>
        <div className="h-full flex flex-col bg-gradient-to-br from-teal-50 via-emerald-50 to-cyan-50 dark:from-violet-950 dark:via-fuchsia-900/30 dark:to-cyan-950 relative overflow-hidden">
          {/* Floating Particles - only in dark mode */}
          <div className="absolute inset-0 pointer-events-none overflow-hidden hidden dark:block">
            <div className="absolute top-20 left-10 text-cyan-300 text-3xl animate-float-particle">✨</div>
            <div className="absolute top-40 right-20 text-pink-400 text-4xl animate-float-particle" style={{ animationDelay: '1s' }}>⭐</div>
            <div className="absolute bottom-40 left-1/4 text-yellow-300 text-3xl animate-float-particle" style={{ animationDelay: '2s' }}>💫</div>
            <div className="absolute bottom-20 right-1/3 text-emerald-300 text-4xl animate-float-particle" style={{ animationDelay: '3s' }}>✨</div>
          </div>
          
          <div className="p-4 border-b border-border dark:border-pink-500/30 bg-card dark:bg-gradient-to-r dark:from-violet-900/50 dark:via-fuchsia-800/50 dark:to-cyan-900/50 backdrop-blur-sm relative z-10">
            <Button 
              variant="outline" 
              onClick={backToMenu}
              size="sm"
              className="border-border dark:border-cyan-500/30 text-foreground dark:text-cyan-200 hover:bg-muted dark:hover:bg-cyan-500/10 dark:hover:text-cyan-100"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Menu
            </Button>
          </div>
          
          <div className="flex-1 overflow-auto p-6 relative z-10">
            <div className="space-y-8 max-w-4xl mx-auto">
              <div className="text-center">
                <div className="flex items-center justify-center gap-4 mb-4">
                  <h1 className="text-4xl font-bold bg-gradient-to-r from-primary via-accent to-primary dark:from-cyan-200 dark:via-pink-300 dark:via-yellow-200 dark:to-purple-300 bg-[length:200%_auto] animate-rainbow-text bg-clip-text text-transparent">
                    🧬 Hatch Evolution Chess
                  </h1>
                  <Button
                    onClick={() => routerNavigate('/hatch-rules')}
                    size="sm"
                    className="text-sm bg-primary hover:bg-primary-hover text-primary-foreground dark:bg-gradient-to-r dark:from-pink-600 dark:via-purple-600 dark:to-cyan-600 dark:hover:from-pink-500 dark:hover:via-purple-500 dark:hover:to-cyan-500 dark:animate-aurora-glow"
                  >
                    📖 Learn Rules
                  </Button>
                </div>
                <p className="text-lg text-foreground dark:text-cyan-100 mb-6">
                  Choose your opponent for this evolutionary battle!
                </p>
              </div>
              
              <ChallengeAISquad
                onShowBotChallenge={(bot) => {
                  setOpponent({ name: bot.name, isBot: true, elo: bot.elo });
                  setGameState('hatch-game');
                }}
              />
            </div>
          </div>
        </div>
      </ChessAppLayout>
    );
  }

  // Hatch Evolution Game
  if (gameState === 'hatch-game') {
    return (
      <ChessAppLayout showGameOptions={false} showFooter={false}>
        <div className="h-full flex flex-col">
          <div className="p-4 border-b border-border">
            <Button 
              variant="outline" 
              onClick={handleBackToMenu}
              size="sm"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Menu
            </Button>
          </div>
          
          <div className="flex-1 overflow-auto">
            {opponent && (
              <HatchEvolutionBoard
                gameMode="hatch"
                opponent={opponent}
                timeControl="10+0"
                onResign={backToMenu}
                onBackToMenu={backToMenu}
              />
            )}
          </div>
        </div>
      </ChessAppLayout>
    );
  }

  if (gameState === 'direct-game' && directGameSession) {
    return (
      <ChessAppLayout showGameOptions={false} showFooter={false}>
        <div className="h-full flex flex-col">
          <div className="p-4 border-b border-border">
            <Button 
              variant="outline" 
              onClick={backToMenu}
              size="sm"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Menu
            </Button>
          </div>
          
          <div className="flex-1 overflow-auto">
            <DirectGameBoard
              gameSessionId={directGameSession.gameSessionId}
              playerColor={directGameSession.playerColor}
            />
          </div>
        </div>
      </ChessAppLayout>
    );
  }

  return (
    <>
      <ChessAppLayout 
        showGameOptions={true}
        showFooter={true}
        onStartGame={startGame}
        onStartEngine={() => setGameState('engine')}
        onStartMultiplayer={() => setGameState('multiplayer')}
        onStartMatchmaking={handleStartMatchmaking}
        onShowBotChallenge={handleGenericBotChallenge}
      >
      <div className="flex flex-col h-full mobile-safe-container">
        <div className="flex-1 p-2 sm:p-4 md:p-6 lg:p-8 space-y-8 md:space-y-12 max-w-7xl mx-auto w-full max-w-full overflow-x-hidden safe-area-left safe-area-right">
          
          {/* Trust Signals - First Impression */}
          <TrustSignals />

          {/* Quick Play Button - Prominent CTA */}
          {user && <QuickPlayButton />}

          {/* Interactive Hero Section - Simplified for Teens */}
          <InteractiveHeroSection 
            onPlayNow={handleGenericBotChallenge}
            onPlayOnline={handleStartMatchmaking}
          />

          {/* Guest Mode Hero - Prominent Call to Action */}
          <GuestModeHero
            onPlayNow={handleGenericBotChallenge}
          />
          
          {/* Challenge AI Squad - Moved to Top */}
          <ChallengeAISquad
            onShowBotChallenge={handleShowBotChallenge}
          />
          
          {/* Game Mode Actions - 3 Card Layout */}
          <GameModeActions
            onEngineMode={() => setGameState('engine')}
            onShowAISquad={handleGenericBotChallenge}
          />
          
          {/* Quick Start Section */}
          <QuickStartSection 
            onQuickPlay={handleGenericBotChallenge}
            onPlayComputer={handleGenericBotChallenge}
            onSolvePuzzle={() => routerNavigate('/puzzles')}
          />
          
          {/* Feature Showcase */}
          <FeatureShowcase />

          {/* Chess Education & Facts */}
          <ChessFactsSection />

          {/* Privacy Banner - Transparency */}
          <PrivacyBanner />
          
          {/* Bot Challenge System Section */}
          <div className="max-w-4xl mx-auto">
            <BotChallengeSystem 
              onShowBotChallenge={handleGenericBotChallenge}
              onStartHatchEvolution={() => {
                setCurrentGameMode('hatch');
                setGameState('hatch-selection');
              }}
            />
          </div>
          
        </div>
      </div>
      
      {/* Onboarding Tour for new users */}
      <OnboardingTrigger autoStart={true} />
      
      </ChessAppLayout>

      <MatchmakingDialog
        open={showMatchmaking}
        onOpenChange={setShowMatchmaking}
        onMatchFound={handleMatchFound}
      />

      <BotChallengeDialog
        open={showBotChallenge}
        onOpenChange={setShowBotChallenge}
        bots={filteredBots}
        selectedBot={selectedBot}
        onChallenge={handleBotChallenge}
      />

      <Dialog open={showAbandonConfirm} onOpenChange={setShowAbandonConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Leave Active Game?</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-muted-foreground">
              You have an active game in progress. Leaving now will not end the game, 
              but you can rejoin anytime before it expires.
            </p>
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                To properly end the game, use the Resign button within the game.
              </AlertDescription>
            </Alert>
            <div className="flex gap-2 justify-end">
              <Button 
                variant="outline" 
                onClick={() => setShowAbandonConfirm(false)}
              >
                Stay in Game
              </Button>
              <Button 
                onClick={confirmAbandonGame}
              >
                Leave to Menu
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default Index;
